import React, { useState, useEffect } from 'react';
import { useTour } from '@reactour/tour';
import { useDemo } from '../context/DemoContext';
import { modelRegistryAPI } from '../api';
import { useActiveModel } from '../context/ActiveModelContext';
import './DemoRunner.css';

function DemoRunnerContent() {
  const {
    isDemoRunning,
    demoStep,
    demoModel,
    demoError,
    isDemoComplete,
    demoConfig,
    startDemo,
    resetDemo
  } = useDemo();

  const { setActiveModel } = useActiveModel();
  const { isOpen, setIsOpen, currentStep, setCurrentStep } = useTour();

  // Update active model when demo model is ready (fetch full metadata if available)
  useEffect(() => {
    const syncActiveModel = async () => {
      if (!demoModel) return;
      const trained = demoModel || {};
      const modelId = trained.model_registry_id || trained.model_id || trained.id;
      if (!modelId) return;
      try {
        const full = await modelRegistryAPI.getModelInfo(modelId).catch(() => null);
        const merged = full ? {
          ...full,
          id: full.id || modelId,
          dataset_name: full.dataset_name || trained.dataset_name || demoConfig.dataset,
          model_type: full.model_type || trained.model_type || demoConfig.modelType,
          metrics: full.metrics || trained.metrics || {}
        } : {
          id: modelId,
          dataset_name: trained.dataset_name || demoConfig.dataset,
          model_type: trained.model_type || demoConfig.modelType,
          trained_date: new Date().toISOString(),
          metrics: trained.metrics || {}
        };
        setActiveModel(merged);
        window.dispatchEvent(new CustomEvent('demo:activeModelSet', { detail: { modelId: merged.id } }));
      } catch (e) {
        setActiveModel({
          id: modelId,
          dataset_name: trained.dataset_name || demoConfig.dataset,
          model_type: trained.model_type || demoConfig.modelType,
          trained_date: new Date().toISOString(),
          metrics: trained.metrics || {}
        });
        window.dispatchEvent(new CustomEvent('demo:activeModelSet', { detail: { modelId } }));
      }
    };
    syncActiveModel();
  }, [demoModel, demoConfig, setActiveModel]);

  // Start the guided tour when demo completes
  useEffect(() => {
    if (isDemoComplete) {
      // Ensure no other tours are active
      window.dispatchEvent(new CustomEvent('demo:closeAllTours'));
      setCurrentStep(0);
      setIsOpen(true);
    }
  }, [isDemoComplete, setIsOpen, setCurrentStep]);

  // Allow external broadcasters to close this tour
  useEffect(() => {
    const handleCloseAll = () => {
      setIsOpen(false);
    };
    window.addEventListener('demo:closeAllTours', handleCloseAll);
    return () => window.removeEventListener('demo:closeAllTours', handleCloseAll);
  }, [setIsOpen]);

  // Advance tour to next step when the user clicks the highlighted target
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      const step = steps[currentStep];
      if (!step || !step.selector) return;
      try {
        const target = document.querySelector(step.selector);
        if (target && (target === e.target || target.contains(e.target))) {
          if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
          } else {
            setIsOpen(false);
          }
        }
      } catch (_) {}
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isOpen, currentStep, setCurrentStep, setIsOpen]);

  const handleStartDemo = () => {
    resetDemo();
    startDemo();
  };

  const handleResetDemo = () => {
    resetDemo();
    setIsOpen(false);
    setCurrentStep(0);
    setActiveModel(null);
    window.dispatchEvent(new CustomEvent('demo:closeAllTours'));
  };

  const handleNextStep = () => {
    const step = steps[currentStep];
    if (step && typeof step.onNext === 'function') {
      step.onNext();
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="demo-runner">
      {/* Demo Control Panel */}
      <div className="demo-controls">
        <div className="demo-info">
          <h3>Interactive Demo</h3>
          <p>Experience the full ML pipeline with a pre-configured dataset.</p>
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Quick start</summary>
            <ol style={{ margin: '8px 0 0 18px', fontSize: 13 }}>
              <li>Click Run Demo to auto-train and set an active model.</li>
              <li>Open Model Registry and validate model health/thresholds.</li>
              <li>Review interpretation confidence, then export artifacts.</li>
            </ol>
          </details>
          
          {demoConfig && (
            <div className="demo-config">
              <h4>Demo Configuration:</h4>
              <ul>
                <li><strong>Dataset:</strong> {demoConfig.dataset}</li>
                <li><strong>Task:</strong> {demoConfig.taskType}</li>
                <li><strong>Model:</strong> {demoConfig.modelType}</li>
                <li><strong>Target:</strong> {demoConfig.targetColumn}</li>
              </ul>
            </div>
          )}

          {demoError && (
            <div className="demo-error">
              <h4>Demo Error:</h4>
              <p>{demoError}</p>
            </div>
          )}

          {isDemoComplete && (
            <div className="demo-success">
              <h4>Demo Complete!</h4>
              <p>You've successfully completed the interactive demo. Try exploring the different tabs to see the results.</p>
            </div>
          )}
        </div>

        <div className="demo-actions">
          {!isDemoRunning ? (
            <button 
              className="demo-button primary-button"
              onClick={handleStartDemo}
            >
              Run Demo
            </button>
          ) : (
            <div className="demo-running">
              <div className="demo-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(demoStep / 3) * 100}%` }}
                  />
                </div>
                <span>Step {demoStep} of 3</span>
              </div>
              
              <div className="demo-status">
                {demoStep === 1 && "Loading demo data..."}
                {demoStep === 2 && "Training model..."}
                {demoStep === 3 && "Demo complete! Follow the guided tour."}
              </div>
            </div>
          )}

          {(isDemoRunning || isDemoComplete) && (
            <button 
              className="secondary-button"
              onClick={handleResetDemo}
            >
              Reset Demo
            </button>
          )}

          {isOpen && (
            <div className="tour-controls">
              <button onClick={handlePrevStep} disabled={currentStep === 0}>
                ← Previous
              </button>
              <button onClick={handleNextStep} disabled={currentStep === steps.length - 1}>
                Next →
              </button>
              <button onClick={() => setIsOpen(false)}>
                End Tour
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

// Tour steps configuration
export const steps = [
  {
    selector: '.model-registry-tab',
    content: 'First, click Model Registry to view available and active models.',
    onNext: () => window.dispatchEvent(new CustomEvent('demo:navigateTab', { detail: { tab: 'Model Registry' } }))
  },
  {
    selector: '.train-model-tab',
    content: 'Next, click Train Model. The demo already trained one for you.',
    onNext: () => window.dispatchEvent(new CustomEvent('demo:navigateTab', { detail: { tab: 'Train Model' } }))
  },
  {
    selector: '.feature-importance-tab',
    content: 'Explore Feature Importance for key drivers.',
    onNext: () => window.dispatchEvent(new CustomEvent('demo:navigateTab', { detail: { tab: 'Feature Importance' } }))
  },
  {
    selector: '.visualizations-tab',
    content: 'View Visualizations and model performance metrics.',
    onNext: () => window.dispatchEvent(new CustomEvent('demo:navigateTab', { detail: { tab: 'Visualizations' } }))
  },
  {
    selector: '.preview-tab',
    content: 'You can always return to Preview to rerun the demo.',
    onNext: () => window.dispatchEvent(new CustomEvent('demo:navigateTab', { detail: { tab: 'Preview' } }))
  }
];

function DemoRunner() {
  return <DemoRunnerContent />;
}

export default DemoRunner;
